import { generatePdf, baseDocumentDefinition, formatDate, standardFooter, horizontalRule, acsfResultsTable } from './common';
import { QUALIFICATION_LEVEL_NAMES, skills } from 'constants';
import { generateLearnerGraph, svgToPng } from 'utils/svg';
import acsfAllPng from 'assets/images/icons/acsf-all.png';

/**
 * Function called to generate course summary PDF.
 * @memberof utils/pdf/Course-Summary
 */
export async function generateCourseSummary(course, units) {
    let docDefinition = await generateDocumentDefinition(course, units);
    generatePdf(docDefinition, `Course Summary - ${course.title} - ${formatDate(new Date())}.pdf`);
}

/**
 * Creates the custom pdfmake Document Definition from the base doc definition and dynamically created content.
 * It adds a header and footer to each page.
 * @memberof utils/pdf/Course-Summary
 */
export async function generateDocumentDefinition(course, units) {
    let docDef = await baseDocumentDefinition(),
        dateGenerated = formatDate(new Date());

    docDef.content.push(await coverPage(course), await unitsTable(course, units));

    return {
        ...docDef,

        footer: standardFooter,

        header: header(dateGenerated),

        pageMargins: [50, 96, 50, 96]
    };
}

/**
 * Generates a header for each page containing the date the report was generated.
 * @memberof utils/pdf/Course-Summary
 */
function header(dateGenerated) {
    return (current) => {
        return current === 1
            ? ''
            : {
                  stack: [
                      {
                          text: 'LLN Report',
                          style: 'subheader'
                      },
                      {
                          text: [
                              {
                                  text: 'Generated: ',
                                  bold: true
                              },
                              dateGenerated
                          ]
                      },
                      horizontalRule()
                  ],
                  margin: [40, 30, 0, 0]
              };
    };
}

/**
 * Puts the client logo, course title and qualification level, date generated,
 * and the course ACSF graph at the top of the first page.
 * @memberof utils/pdf/Course-Summary
 */
async function coverPage(course) {
    return [
        {
            image: 'client-logo',
            style: 'centered',
            fit: [300, 150],
            margin: [0, -40, 0, 10],
            width: 100
        },
        {
            text: `${course.title} - ${QUALIFICATION_LEVEL_NAMES[course.level]}`,
            style: ['subheader', 'centered'],
            margin: [0, 0, 0, 10]
        },
        await makeGraph(course.profile),
        { text: '', margin: [0, 15] }
    ];
}

/**
 * Provides the ACSF graph for the course.
 * @memberof utils/pdf/Course-Summary
 */
async function makeGraph(profile) {
    try {
        return {
            image: await svgToPng(await generateLearnerGraph([], profile)),
            width: 250,
            alignment: 'center'
        };
    } catch (e) {
        return acsfResultsTable(profile);
    }
}

/**
 * Creates table header for units summary.
 * @memberof utils/pdf/Course-Summary
 */
function unitsTable(course, units) {
    return [
        {
            table: {
                headerRows: 1,
                widths: ['auto', '*', 'auto'],
                body: [
                    [
                        {
                            text: 'Unit Code',
                            bold: true
                        },
                        {
                            text: 'Unit Title',
                            bold: true
                        },
                        {
                            image: acsfAllPng,
                            width: 120
                        }
                    ],
                    ...unitSpread(units, course.level)
                ]
            }
        }
    ];
}

/**
 * Creates a table row for each unit.
 * @memberof utils/pdf/Course-Summary
 */
function unitSpread(units, level) {
    return units.map((unit) => {
        return [
            {
                text: unit.unitcode || 'N/A',
                fontSize: 10
            },
            { text: unit.title || 'N/A' },
            {
                columns: profileGetter(unit, level),
                style: 'centered'
            }
        ];
    });
}

/**
 * Returns the AQF profile for the unit level passed in.
 * @memberof utils/pdf/Course-Summary
 */
function profileGetter(unit, level) {
    let { profiles } = unit;
    let unitProfile = profiles.find((profile) => profile.level === level);
    if (unitProfile == null) {
        throw new Error(`Could not find profile level ${level} within unit ${unit.unitcode}`);
    }
    let { profile } = unitProfile;
    return skills.map((skill) => ({ text: profile[skill].toString() }));
}
